import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { exchangeGuestCode, consumeGuestReturnTo, safeInternalPath } from '@/shared/auth/cognito';
import { useAuthStore } from '@/shared/auth/useAuthStore';
import { useNotificationStore } from '@/shared/api/notificationStore';
import { axiosClient } from '@/shared/api/axiosClient';
import { routePaths } from '@/app/router/routePaths';
import type { User } from '@/entities/user/model/user.types';
import type { ApiSuccess } from '@/shared/api/apiResponse.types';

export function AuthGuestCallbackPage() {
  const navigate = useNavigate();
  const { setTokens, setUser, clear } = useAuthStore();
  const notify = useNotificationStore((s) => s.show);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const search = new URLSearchParams(window.location.search);
    const code = search.get('code');
    if (!code) {
      notify('Sign-in was cancelled or failed. Please try again.', 'error');
      navigate(routePaths.landing, { replace: true });
      return;
    }

    (async () => {
      try {
        const tokens = await exchangeGuestCode(code);
        setTokens({
          idToken: tokens.id_token,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
          client: 'guest',
        });

        const res = await axiosClient.get<ApiSuccess<User>>('/me', {
          headers: { Authorization: `Bearer ${tokens.id_token}` },
        });
        setUser(res.data.data);

        // Return to the event page the guest came from. Prefer the OAuth
        // state param (survives tab/browser changes during verification);
        // fall back to sessionStorage, then landing.
        const returnTo = safeInternalPath(search.get('state'))
          ?? consumeGuestReturnTo()
          ?? routePaths.landing;
        navigate(returnTo, { replace: true });
      } catch (err) {
        console.error('Guest auth callback failed', err);
        clear();
        notify('Sign-in failed. Please try again.', 'error');
        navigate(routePaths.landing, { replace: true });
      }
    })();
  }, [navigate, setTokens, setUser, clear, notify]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: 'background.default' }}>
      <CircularProgress />
      <Typography color="text.secondary">Getting you in…</Typography>
    </Box>
  );
}
